import React, { useContext, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiArrowRight, FiMinus, FiPlus, FiTrash2 } from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/cartpage.scss";

const CartPage = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity } = useContext(CartContext);

  useEffect(() => {
    document.title = `Shopping bag | ${siteConfig.brandName}`;
  }, []);

  const lines = useMemo(
    () =>
      cartItems
        .map((item) => ({
          ...item,
          product: productData.find((product) => product.id === item.id),
        }))
        .filter((item) => item.product),
    [cartItems]
  );

  const subtotal = lines.reduce(
    (total, item) => total + item.product.price * item.quantity,
    0
  );
  const shipping =
    subtotal >= siteConfig.freeShipping.threshold
      ? 0
      : siteConfig.shipping.flatRate;
  const total = subtotal + shipping;
  const amountToFreeShipping = Math.max(
    0,
    siteConfig.freeShipping.threshold - subtotal
  );
  const shippingProgress = Math.min(
    100,
    (subtotal / siteConfig.freeShipping.threshold) * 100
  );

  if (lines.length === 0) {
    return (
      <main className="cart-page cart-page--empty">
        <div className="cart-page__empty-box">
          <p className="eyebrow">Your bag</p>
          <h1>Your shopping bag is empty</h1>
          <p>
            Explore soft layers, quiet tailoring and new pieces made for every day.
          </p>
          <Link to="/shop" className="cart-primary-link">
            Continue shopping <FiArrowRight aria-hidden="true" />
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="cart-page">
      <header className="cart-page__header">
        <p className="eyebrow">Your bag</p>
        <h1>Shopping bag</h1>
        <span>
          {lines.reduce((count, item) => count + item.quantity, 0)} item(s)
        </span>
      </header>

      <div className="cart-page__layout">
        <section className="cart-page__left" aria-label="Shopping bag items">
          <div className="shipping-progress">
            <p>
              {amountToFreeShipping > 0
                ? `Add ${formatPrice(amountToFreeShipping)} more for free delivery.`
                : "Your order qualifies for free delivery."}
            </p>
            <div aria-hidden="true">
              <span style={{ width: `${shippingProgress}%` }} />
            </div>
          </div>

          <div className="cart-items">
            {lines.map((item) => {
              const { product } = item;
              return (
                <article
                  className="cart-item"
                  key={`${item.id}-${item.size}-${item.color}`}
                >
                  <Link to={`/shopDetails/${product.id}`} className="cart-item__thumb">
                    <img src={product.image} alt={product.name} />
                  </Link>
                  <div className="cart-item__info">
                    <div>
                      <Link to={`/shopDetails/${product.id}`}>{product.name}</Link>
                      <p>{formatPrice(product.price)}</p>
                    </div>
                    <dl className="cart-item__meta">
                      <div>
                        <dt>Color</dt>
                        <dd>{item.color}</dd>
                      </div>
                      <div>
                        <dt>Size</dt>
                        <dd>{item.size}</dd>
                      </div>
                    </dl>
                    <div className="cart-item__footer">
                      <div className="cart-qty" aria-label={`Quantity for ${product.name}`}>
                        <button
                          type="button"
                          onClick={() =>
                            updateQuantity(
                              item.id,
                              item.size,
                              item.color,
                              item.quantity - 1
                            )
                          }
                          disabled={item.quantity === 1}
                          aria-label="Decrease quantity"
                        >
                          <FiMinus />
                        </button>
                        <span>{item.quantity}</span>
                        <button
                          type="button"
                          onClick={() =>
                            updateQuantity(
                              item.id,
                              item.size,
                              item.color,
                              item.quantity + 1
                            )
                          }
                          aria-label="Increase quantity"
                        >
                          <FiPlus />
                        </button>
                      </div>
                      <button
                        type="button"
                        className="cart-remove"
                        onClick={() =>
                          removeFromCart(item.id, item.size, item.color)
                        }
                      >
                        <FiTrash2 aria-hidden="true" /> Remove
                      </button>
                      <strong>{formatPrice(product.price * item.quantity)}</strong>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          <Link to="/shop" className="cart-continue">
            Continue shopping
          </Link>
        </section>

        <aside className="cart-page__right">
          <div className="summary">
            <h2>Order summary</h2>
            <div className="summary-row">
              <span>Subtotal</span>
              <span>{formatPrice(subtotal)}</span>
            </div>
            <div className="summary-row">
              <span>Delivery</span>
              <span>{shipping === 0 ? "Free" : formatPrice(shipping)}</span>
            </div>
            <div className="summary-total">
              <div>
                <span>Total</span>
                <small>VAT included</small>
              </div>
              <strong>{formatPrice(total)}</strong>
            </div>
            <button
              className="place-order"
              type="button"
              onClick={() => navigate("/checkout")}
            >
              Check out · {formatPrice(total)}
            </button>
            <ul className="summary-usps">
              <li>Secure demo checkout</li>
              <li>Easy returns within {siteConfig.returns.windowDays} days</li>
              <li>Need help? {siteConfig.contact.email}</li>
            </ul>
          </div>
        </aside>
      </div>
    </main>
  );
};

export default CartPage;
